import { Clock3, ShieldCheck, MessagesSquare, Mail, Instagram, Linkedin } from "lucide-react";
import { EnquiryForm } from "@/components/EnquiryForm";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/components/SEO";

const NOTES = [
  { icon: Clock3, title: "Response time", text: "Enquiries are picked up on working days. An advisor typically calls back within one business day." },
  { icon: ShieldCheck, title: "Your data", text: "Shared only with lending partners relevant to your enquiry, as described in our Privacy Policy." },
  { icon: MessagesSquare, title: "Every question welcome", text: "Not sure which loan fits? Ask anyway — that is literally our job." },
];

export default function Contact() {
  useSEO({
    title: "Contact Loan Salaah — Talk to a Loan Advisor",
    description: "Reach Loan Salaah for any loan question. Send an enquiry and an advisor will call you back, usually within one working day.",
  });

  return (
    <div data-testid="contact-page" className="pb-24 pt-36">
      <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <Reveal className="lg:col-span-5">
          <div className="flex items-center gap-4">
            <span className="brass-rule" aria-hidden="true" />
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">Contact</span>
          </div>
          <h1 className="mt-5 font-serif text-4xl font-semibold tracking-tight text-concordia sm:text-5xl">
            Say hello. We'll take it from there.
          </h1>
          <p className="mt-5 text-base leading-relaxed text-slate">
            Questions about a loan, your eligibility, or how a DSA works — send the form and a real human advisor
            calls you back.
          </p>
          <ul className="mt-10 space-y-6">
            {NOTES.map((n) => (
              <li key={n.title} className="flex gap-4" data-testid={`contact-note-${n.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-concordia/[0.07] text-concordia">
                  <n.icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-charcoal">{n.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate">{n.text}</p>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-10 rounded-2xl border border-sand bg-white p-6" data-testid="contact-direct-email-card">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-concordia/[0.07] text-concordia">
                <Mail className="h-5 w-5" />
              </span>
              <p className="text-sm font-semibold text-charcoal">Prefer email?</p>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate">
              Send your loan or credit card application directly to{" "}
              <a href="mailto:info@loansalaah.com" data-testid="contact-applications-email-link"
                className="font-semibold text-concordia underline decoration-brass/50 underline-offset-2">
                info@loansalaah.com
              </a>{" "}
              — include your name, phone, product and amount, and it lands straight in our applications inbox.
            </p>
          </div>

          <div className="mt-8 flex items-center gap-4">
            <p className="font-mono text-[0.62rem] uppercase tracking-[0.3em] text-brass">Follow us</p>
            <a href="https://www.instagram.com/loan_salaah/" target="_blank" rel="noopener noreferrer"
              data-testid="contact-instagram-link" aria-label="Loan Salaah on Instagram"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-sand text-concordia transition-all duration-300 hover:border-brass hover:text-brass-deep">
              <Instagram className="h-4 w-4" />
            </a>
            <a href="https://www.linkedin.com/company/loan-salaah/" target="_blank" rel="noopener noreferrer"
              data-testid="contact-linkedin-link" aria-label="Loan Salaah on LinkedIn"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-sand text-concordia transition-all duration-300 hover:border-brass hover:text-brass-deep">
              <Linkedin className="h-4 w-4" />
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.12} className="lg:col-span-7">
          <EnquiryForm prefill={{}} />
        </Reveal>
      </div>
    </div>
  );
}
